import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import {
  Spacer, LogoUA, ButtonMenu, Avatar,
} from 'components/atoms';
import { Tooltip } from 'components/molecules'
import { SubwebsList } from 'components/organisms';
import { sitesType, userType, iconType } from 'types';
import { Link } from 'react-router-dom';

/**
 * Global Navigation: Change between subwebs
 */

const NavGlobal = ({
  isOpen,
  shouldOpenOnHover,
  onMouseEnter,
  onMouseLeave,
  sites,
  onFocusSearch,
  onBlurSearch,
  user,
  subweb,
  bottomActions,
}) => {
  const [isHover, setIsHover] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(isOpen);

  useEffect(() => {
    if (shouldOpenOnHover && !isOpen) setIsNavOpen(isHover);
  }, [isHover, shouldOpenOnHover, isOpen]);

  useEffect(() => {
    setIsNavOpen(isOpen);
  }, [isOpen]);

  const computedLogoVersion = useMemo(() => (isNavOpen ? 'desktop' : 'icon'), [
    isNavOpen,
  ]);

  const computedButtonModifiers = useMemo(
    () => (isNavOpen
      ? ['clean', 'primaryAlt', 'globalNav', 'clearMargin']
      : ['clean', 'primaryAlt', 'iconOnly', 'center', 'clearMargin']),
    [isNavOpen]
  );

  const handleMouseEnter = e => {
    setIsHover(true);
    onMouseEnter(e);
  };

  const handleMouseLeave = e => {
    setIsHover(false);
    onMouseLeave(e);
  };

  return (
    <Wrapper
      onMouseEnter={e => handleMouseEnter(e)}
      onMouseLeave={e => handleMouseLeave(e)}
      isNavOpen={isNavOpen}
    >
      <Wrapper.Top>
        <Link to="/" aria-label="Página inicial">
          <Tooltip tooltip="Home" placement="right" hideArrow>
            <Spacer spacing="l" top="xl">
              <LogoUA version={computedLogoVersion} size="medium" />
            </Spacer>
          </Tooltip>
        </Link>
        <Wrapper.Center>
          <SubwebsList
            stretch={isNavOpen}
            sites={sites}
            onFocusSearch={onFocusSearch}
            onBlurSearch={onBlurSearch}
            activeSubweb={subweb}
          />
        </Wrapper.Center>
      </Wrapper.Top>
      <Wrapper.Bottom>
        {bottomActions.map((action) => {
          if (action.isDisabled) {
            return (
              <Tooltip key={action.title} tooltip={action.tooltip || action.title} placement="right-end" hideArrow>
                <ButtonMenu
                  icon={action.icon}
                  title={action.title}
                  modifiers={[...computedButtonModifiers, 'disabled']}
                  iconAltStyle
                >
                  {action.title}
                </ButtonMenu>
              </Tooltip>
            )
          }

          return (
            <Link to={action.to} key={action.title} aria-label={`ir para ${action.title}`}>
              <Tooltip tooltip={action.tooltip || action.title} placement="right-end" hideArrow>
                <ButtonMenu
                  icon={action.icon}
                  title={action.title}
                  modifiers={computedButtonModifiers}
                  iconAltStyle
                >
                  {action.title}
                </ButtonMenu>
              </Tooltip>
            </Link>
          )
        })}
        {user && (
          <Link to="/profile" aria-label="ir para definições de utilizador">
            <Tooltip tooltip={user.name} placement="right-end" hideArrow>
              <AvatarWrapper isNavOpen={isNavOpen}>
                <Avatar name={user.name} image={user.image} showName={isNavOpen} />
              </AvatarWrapper>
            </Tooltip>

          </Link>
        )}
      </Wrapper.Bottom>
    </Wrapper>
  );
};

NavGlobal.propTypes = {
  isOpen: PropTypes.bool,
  shouldOpenOnHover: PropTypes.bool,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  sites: sitesType,
  bottomActions: PropTypes.arrayOf(
    PropTypes.shape({
      to: PropTypes.string,
      icon: iconType,
      title: PropTypes.string,
    })
  ),
  onFocusSearch: PropTypes.func,
  onBlurSearch: PropTypes.func,
  user: userType,
  subweb: PropTypes.string,
};

NavGlobal.defaultProps = {
  isOpen: false,
  shouldOpenOnHover: true,
  onMouseEnter: () => null,
  onMouseLeave: () => null,
  sites: [],
  onFocusSearch: () => null,
  onBlurSearch: () => null,
  user: {
    name: '',
  },
  subweb: null,
  bottomActions: [],
};

const Wrapper = styled.div`
  position: relative;
  background-color: ${props => props.theme.colors.bg};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: ${props => (props.isNavOpen
    ? props.theme.components.navGlobal.width
    : props.theme.components.navGlobal.widthClosed)};
  a {
    text-decoration: none;
  }
`;

Wrapper.Top = styled.div`
  border-right: ${props => props.theme.values.borderSizeM} solid
    ${props => props.theme.colors.borderLighter};
  height: 100%;
`;

// Align to page subtitle
Wrapper.Center = styled.div`
  padding-top: 66px;
`;

Wrapper.Bottom = styled.div`
  padding-bottom: ${props => props.theme.spacing.xl3};
  border-right: ${props => props.theme.values.borderSizeM} solid
    ${props => props.theme.colors.borderLighter};
`;

const AvatarWrapper = styled.div`
  display: flex;
  justify-content: ${props => (props.isNavOpen ? 'flex-start' : 'center')};
  width: 100%;
  padding: ${props => props.theme.spacing.xl};
  padding-top: ${props => props.theme.spacing.xs};
  padding-bottom: ${props => props.theme.spacing.xs};
`;

export default NavGlobal;
